import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { Permission } from '@cmms/types'
import { api } from '../lib/api'
import { useAuthStore } from '../store/authStore'

interface PermCtx {
  permissions: Set<string>
  loading: boolean
  /**
   * Returns true if the user has the given permission.
   *   - Super admins always return true (they bypass RBAC).
   *   - While permissions are loading, returns true (optimistic).
   *   - Once loaded, checks the real permission set.
   */
  can: (permission: string) => boolean
  canAny: (...permissions: string[]) => boolean
  canAll: (...permissions: string[]) => boolean
  /** True when the current user is a super admin. */
  isSuperAdmin: boolean
}

const Ctx = createContext<PermCtx>({
  permissions: new Set(),
  loading: true,
  can: () => true,
  canAny: () => true,
  canAll: () => true,
  isSuperAdmin: false,
})

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user)
  const isSuperAdmin = user?.type === 'super_admin'
  const roleName = user?.role

  // Super admins bypass the entire role→permission fetch chain.
  // They always get can() = true.
  const shouldFetchRoles = Boolean(roleName) && !isSuperAdmin

  const rolesQ = useQuery({
    queryKey: ['roles'],
    queryFn: () => api.roles.list(),
    enabled: shouldFetchRoles,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

  const roleId = useMemo(
    () => (rolesQ.data ?? []).find((r) => r.name === roleName)?.id,
    [rolesQ.data, roleName],
  )

  const roleDetailQ = useQuery({
    queryKey: ['role-detail', roleId],
    queryFn: () => api.roles.get(roleId!),
    enabled: Boolean(roleId) && !isSuperAdmin,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  })

  const value = useMemo<PermCtx>(() => {
    // Super admins have all permissions by definition.
    if (isSuperAdmin) {
      return {
        permissions: new Set(),
        loading: false,
        can: () => true,
        canAny: () => true,
        canAll: () => true,
        isSuperAdmin: true,
      }
    }

    const stillLoading = rolesQ.isLoading || (Boolean(roleId) && roleDetailQ.isLoading)
    const loaded = !stillLoading && Boolean(roleDetailQ.data)
    const perms = new Set(
      (roleDetailQ.data?.permissions ?? []).map((p: Permission) => p.name),
    )

    return {
      permissions: perms,
      loading: stillLoading,
      isSuperAdmin: false,
      // While loading or if the fetch failed, default to true so buttons aren't
      // disabled during the brief loading window.
      can: (p) => loaded ? perms.has(p) : true,
      canAny: (...ps) => loaded ? ps.some((p) => perms.has(p)) : true,
      canAll: (...ps) => loaded ? ps.every((p) => perms.has(p)) : true,
    }
  }, [isSuperAdmin, roleDetailQ.data, rolesQ.isLoading, roleDetailQ.isLoading, roleId])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function usePermissions() {
  return useContext(Ctx)
}

/**
 * Declarative guard.
 * - Default: always renders children (use `disabled` pattern instead).
 * - With `hide`: fully removes children when permission is denied.
 */
export function Can({
  permission,
  any,
  all,
  hide = false,
  fallback = null,
  children,
}: {
  permission?: string
  any?: string[]
  all?: string[]
  hide?: boolean
  fallback?: ReactNode
  children: ReactNode
}) {
  const { can, canAny, canAll } = usePermissions()

  let allowed = true
  if (permission) allowed = can(permission)
  if (any)        allowed = canAny(...any)
  if (all)        allowed = canAll(...all)

  if (!allowed && hide) return <>{fallback}</>
  return <>{children}</>
}
